/**
 * Open the host Cuoral chat via official programmatic API.
 * Cuoral FAB is hidden (Help menu owns entry); widget stays loaded.
 */

type CuoralWidgetApi = {
  open?: () => void;
  close?: () => void;
  hideFab?: () => void;
};

declare global {
  interface Window {
    CuoralWidget?: CuoralWidgetApi;
  }
}

function tryOpenCuoralApi(): boolean {
  const api = typeof window !== "undefined" ? window.CuoralWidget : undefined;
  if (typeof api?.open !== "function") return false;
  api.open();
  // Keep launcher demoted after open (API may re-show FAB).
  if (typeof api.hideFab === "function") {
    api.hideFab();
  }
  return true;
}

/** Best-effort DOM click fallback if API not ready. */
function tryOpenCuoralDom(): boolean {
  if (typeof document === "undefined") return false;

  const selectors = [
    "iframe[src*='cuoral']",
    "#cuoral-widget",
    ".cuoral-launcher",
    "[class*='cuoral'][class*='button']",
    "[class*='Cuoral']",
  ];

  for (const selector of selectors) {
    const el = document.querySelector(selector);
    if (!el) continue;
    if (el instanceof HTMLIFrameElement) {
      const host =
        el.parentElement?.querySelector("button, [role='button'], a") ??
        el.parentElement;
      if (host instanceof HTMLElement) {
        host.click();
        return true;
      }
      continue;
    }
    if (el instanceof HTMLElement) {
      el.click();
      return true;
    }
  }

  return false;
}

function tryOpenOnce(): boolean {
  return tryOpenCuoralApi() || tryOpenCuoralDom();
}

/** Sync attempt — use when the widget is already loaded. */
export function openCuoralSupport(): boolean {
  if (typeof window === "undefined") return false;
  return tryOpenOnce();
}

/** Awaitable open — preferred from Help chrome so toast is accurate. */
export async function openCuoralSupportAsync(
  timeoutMs = 2500,
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (tryOpenOnce()) return true;

  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    await new Promise((r) => setTimeout(r, 200));
    if (tryOpenOnce()) return true;
  }
  return false;
}
